// tkneDrift.ts
//
// PURE, dependency-free drift-math module for the TKNE project drift system.
// No imports besides types (none needed). Safe to unit test in isolation and
// reusable from any layer (UI, hooks, server) without pulling in React/Expo/etc.
//
// THE MECHANIC
// ------------
// A project has 5 stages in progression order, index 0..4:
//   0 = Step, 1 = Today, 2 = Aim, 3 = Goal, 4 = Endeavour
// "Up"/cascade direction is toward higher index (Step -> Endeavour): a LOWER
// stage (lower index) pushes drift onto the stage ABOVE it (next higher index).
//
// A COMPLETE stage (checked === true) never drifts (0 segments), and does not
// propagate anything upward.
//
// SEGMENTS accrue at 1 segment per CYCLE a stage is overdue, past a grace gap:
//   daysOverdue = max(0, floor((now - dueDate) / day) - gapDays)
//   ownSegments = clamp(floor(daysOverdue / cycleDays), 0, 8)
// (dueDate null, or stage checked -> ownSegments = 0.)
//
// SCORE: score = floor(segments / 2), range 0..4 (8 segments = score 4).
//
// CASCADE (Step -> Endeavour): walk from index 0 upward.
//   effectiveSegments[0] = ownSegments[0]
//   effectiveSegments[i] = min(8, ownSegments[i] + floor(effectiveSegments[i-1] / 2))
//
// MANUAL OVERRIDE: an optional per-stage override (number | null) — when a
// stage's override is non-null, it is used as that stage's SEGMENTS directly
// (bypassing the date computation), and still feeds the cascade.
//
// CASCADE REACH: drift keeps climbing. Any stage carrying effective drift
// (own OR inherited) passes floor(/2) of it to the stage above, so a badly
// late early stage ultimately drags the whole chain toward Endeavour — even
// through stages that aren't individually overdue. The grace gap governs when
// a stage ACCRUES its own segments (computeOwnSegments), which is the
// "propagation delay"; once drift exists it propagates the rest of the way.
//
// COMPLETED-STAGE FIREWALL: a checked stage is 0 and blocks drift from below
// passing through it — later stages aren't dragged by what preceded a
// finished stage.

export interface DriftStageInput {
  dueDate: string | null;
  checked: boolean;
  override?: number | null;
}

export interface DriftResult {
  segments: number;
  score: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SEGMENT_CAP = 8;

/** Clamp a number into [min, max]. */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** score = floor(segments / 2), range 0..4 (8 segments = score 4). */
export function scoreFromSegments(segments: number): number {
  return clamp(Math.floor(segments / 2), 0, 4);
}

/**
 * Compute a single stage's own segments (pre-cascade) from either its manual
 * override or its date-based overdue computation. A checked stage or one with
 * no due date contributes 0.
 */
function computeOwnSegments(
  stage: DriftStageInput,
  now: Date,
  cycleDays: number,
  gapDays: number,
): number {
  if (stage.checked) return 0;

  if (stage.override !== undefined && stage.override !== null) {
    return clamp(Math.floor(stage.override), 0, SEGMENT_CAP);
  }

  if (!stage.dueDate) return 0;

  const due = new Date(stage.dueDate);
  if (Number.isNaN(due.getTime())) return 0;

  const rawDaysLate = Math.floor((now.getTime() - due.getTime()) / MS_PER_DAY);
  const daysOverdue = Math.max(0, rawDaysLate - gapDays);
  const safeCycleDays = cycleDays > 0 ? cycleDays : 1;
  return clamp(Math.floor(daysOverdue / safeCycleDays), 0, SEGMENT_CAP);
}

/**
 * Compute per-stage drift (segments + score) for a 5-stage TKNE project,
 * applying the Step -> Endeavour cascade and grace-gap propagation delay.
 *
 * `stages` must be in progression order: [Step, Today, Aim, Goal, Endeavour].
 * Returns results in the same order.
 */
export function computeProjectDrift(
  stages: DriftStageInput[],
  opts: { now: Date; cycleDays: number; gapDays: number },
): DriftResult[] {
  const { now, cycleDays, gapDays } = opts;

  const own = stages.map((stage) => computeOwnSegments(stage, now, cycleDays, gapDays));

  const effectiveSegments: number[] = [];

  for (let i = 0; i < stages.length; i++) {
    const ownSegments = own[i];

    // A completed stage is a firewall: it never drifts AND blocks drift from
    // below passing through it (you've finished it — later stages aren't
    // dragged by what preceded it).
    if (stages[i].checked) {
      effectiveSegments[i] = 0;
      continue;
    }

    if (i === 0) {
      effectiveSegments[i] = ownSegments;
    } else {
      // Drift cascades all the way up: any stage carrying effective drift
      // (its own OR drift inherited from stages below it) passes half of it
      // (floor/2) onward to the stage above. So a badly-late early stage
      // ultimately drags the whole chain toward Endeavour, even through
      // stages that aren't individually overdue. The grace gap still governs
      // whether a stage ACCRUES its own segments (in computeOwnStage), which
      // is the "propagation delay" — but once drift exists, it keeps climbing.
      const belowPushes = effectiveSegments[i - 1] > 0
        ? Math.floor(effectiveSegments[i - 1] / 2)
        : 0;
      effectiveSegments[i] = clamp(ownSegments + belowPushes, 0, SEGMENT_CAP);
    }
  }

  return stages.map((_, i) => ({
    segments: effectiveSegments[i],
    score: scoreFromSegments(effectiveSegments[i]),
  }));
}
